import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import s from './styles/PersonalData.module.scss'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as PhoneIcon } from 'assets/icons/phone.svg'
import { ReactComponent as MailIcon } from 'assets/icons/mail.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check_star_accept.svg'
import { ReactComponent as PlanetIcon } from 'assets/icons/planet.svg'
import { ReactComponent as UniversityIcon } from 'assets/icons/university.svg'
import { ReactComponent as BagIcon } from 'assets/icons/bag_empty.svg'
import { ReactComponent as DockPenIcon } from 'assets/icons/dock_pen.svg'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import usePersonalData from './hooks/usePersonalData'
import Button from 'shared/ui/Button/Button'

const COLLAPSED_COUNT = 3

const TagList = ({ tags, emptyText }) => {
	const [expanded, setExpanded] = useState(false)

	if (tags.length === 0) {
		return <span className={s.empty}>{emptyText}</span>
	}

	const visibleTags = expanded ? tags : tags.slice(0, COLLAPSED_COUNT)
	const hiddenCount = tags.length - COLLAPSED_COUNT

	return (
		<div className={s.tags}>
			{visibleTags}
			{hiddenCount > 0 && (
				<button
					type="button"
					className={s.more}
					onClick={() => setExpanded(prev => !prev)}
				>
					{expanded ? 'Hide' : `+${hiddenCount} more`}
				</button>
			)}
		</div>
	)
}

const PersonalData = observer(
	({
		studentData = {},
		onOpenAssignActivity,
		onOpenAssignEssay,
	}) => {
		const {
			name,
			grade,
			phone,
			email,
			countryList,
			universitiesList,
			careersList,
			isMobile,
			avatarKey,
			avatarSource,
			photoUrl,
		} = usePersonalData({
			studentData,
		})

		const passportCountry = String(
			studentData?.nationality || studentData?.passport_country || '',
		).trim()
		const avatarSize = isMobile ? 48 : 60
		const buttonSize = isMobile ? 'sm' : 'md'

		return (
			<section className={s.card}>
				<div className={s.header}>
					<div className={s.profile}>
						<div className={s.info}>
							<div className={s.avatar}>
								{name && (
									<InitialAvatar
										name={name}
										size={avatarSize}
										avatarKey={avatarKey}
										avatarSource={avatarSource}
										photoUrl={photoUrl}
									/>
								)}
							</div>
							<div className={s.info__inner}>
								<h1 className={s.name} title={name}>
									{name}
								</h1>
								{grade && <span className={s.grade}>Grade {grade}</span>}
							</div>
						</div>
						<div className={s.contacts}>
							{phone && (
								<div className={s.contact}>
									<span className={s.contact__icon}>
										<PhoneIcon />
									</span>
									<span className={s.contact__text}>{phone}</span>
								</div>
							)}
							{email && (
								<div className={s.contact}>
									<span className={s.contact__icon}>
										<MailIcon />
									</span>
									<span className={s.contact__text}>{email}</span>
								</div>
							)}
							{/* Passport is the student's nationality. `country` is their first target
							    (destination) country, so it must never be shown here. */}
							<div className={s.passport}>
								<span className={s.passport__icon}>
									<CheckIcon />
								</span>
								<span className={s.passport__text}>
									Passport: {passportCountry || 'Not provided'}
								</span>
							</div>
						</div>
					</div>

					<div className={s.divider} />

					<div className={s.preferences}>
						<div className={s.group}>
							<div className={s.group__head}>
								<span className={s.group__icon}>
									<PlanetIcon />
								</span>
								<span className={s.group__label}>Countries</span>
							</div>
							<TagList
								emptyText="No countries selected"
								tags={countryList.map(({ FlagIcon, name, iso2 }, index) => (
									<span
										key={iso2 || name || index}
										className={s.countryPill}
										data-country={iso2 ? String(iso2).toLowerCase() : undefined}
									>
										<span className={s.countryPill__flag} aria-hidden="true">
											<FlagIcon />
										</span>
										<span className={s.countryPill__name}>{name}</span>
									</span>
								))}
							/>
						</div>

						<div className={s.group}>
							<div className={s.group__head}>
								<span className={s.group__icon}>
									<UniversityIcon />
								</span>
								<span className={s.group__label}>Universities</span>
							</div>
							<TagList
								emptyText="No universities selected"
								tags={universitiesList.map((university, index) => (
									<span key={index} className={s.tag}>
										<span className={s.tag__val}>{university}</span>
									</span>
								))}
							/>
						</div>

						<div className={s.group}>
							<div className={s.group__head}>
								<span className={s.group__icon}>
									<BagIcon />
								</span>
								<span className={s.group__label}>Career Interests</span>
							</div>
							<TagList
								emptyText="No career selected"
								tags={careersList.map((career, index) => (
									<span key={index} className={s.tag}>
										<span className={s.tag__val}>{career}</span>
									</span>
								))}
							/>
						</div>
					</div>
				</div>

				<div className={s.actions}>
					<div className={s.assignments}>
						<Button
							leftIcon={<DockPenIcon />}
							variant="primary"
							onClick={onOpenAssignEssay}
							size={buttonSize}
						>
							Assign Essay
						</Button>
						<Button
							leftIcon={<DockIcon />}
							variant="white"
							onClick={onOpenAssignActivity}
							size={buttonSize}
						>
							Assign Activity
						</Button>
					</div>
				</div>
			</section>
		)
	},
)

export default PersonalData
